// Board display logic (milestone L5.2). Pure. Times shown here are differences between clinical
// times and the clock, for display only; alert levels come from the rules engine's events and
// are never decided here (CLAUDE.md hard rule 3).

import type { BoardEntry, BoardTimer } from '@diagnuvo/domain';

export type BoardPatch =
  { op: 'upsert'; entry: BoardEntry } | { op: 'remove'; siteId: string; encounterId: string };

export type ServerMessage =
  | { type: 'snapshot'; siteId: string; entries: BoardEntry[] }
  | { type: 'patch'; patch: BoardPatch };

export type Rows = ReadonlyMap<string, BoardEntry>;

/** A snapshot replaces every row; a patch changes one. Applying a patch twice is harmless. */
export function applyMessage(rows: Rows, message: ServerMessage): Rows {
  if (message.type === 'snapshot') {
    return new Map(message.entries.map((e) => [e.encounterId, e]));
  }
  const next = new Map(rows);
  const { patch } = message;
  if (patch.op === 'upsert') next.set(patch.entry.encounterId, patch.entry);
  else next.delete(patch.encounterId);
  return next;
}

const ZONES: readonly { code: string; label: string }[] = [
  { code: 'RES', label: 'Resus' },
  { code: 'MAJ', label: 'Majors' },
  { code: 'MIN', label: 'Minors' },
  { code: 'PAED', label: 'Paediatrics' },
  { code: 'TRI', label: 'Triage' },
  { code: 'WR', label: 'Waiting room' },
];

/** Clinical order of the zones (Resus first); unknown zones after them. */
export function zoneRank(code: string): number {
  const i = ZONES.findIndex((z) => z.code === code);
  return i < 0 ? ZONES.length : i;
}

export interface Zone {
  code: string;
  label: string;
  rows: BoardEntry[];
}

/** Rows by zone (location room), known zones first, longest wait first within a zone. */
export function byZone(rows: Rows): Zone[] {
  const groups = new Map<string, BoardEntry[]>();
  for (const row of rows.values()) {
    const code = row.location?.room || '';
    groups.set(code, [...(groups.get(code) ?? []), row]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => zoneRank(a) - zoneRank(b) || a.localeCompare(b))
    .map(([code, list]) => ({
      code,
      label: ZONES.find((z) => z.code === code)?.label ?? (code || 'No location'),
      rows: list.sort((x, y) => (x.arrivedAt ?? '').localeCompare(y.arrivedAt ?? '')),
    }));
}

/** h:mm for a non-negative duration in milliseconds. */
export function hoursMinutes(ms: number): string {
  const minutes = Math.floor(Math.max(ms, 0) / 60_000);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

export function timeInDepartment(row: BoardEntry, now: number): string | undefined {
  return row.arrivedAt === undefined ? undefined : hoursMinutes(now - Date.parse(row.arrivedAt));
}

export interface Countdown {
  text: string;
  /** The recorded alert, from the rules engine. */
  alert: BoardTimer['alert'];
}

/**
 * What staff and the rules engine recorded about the timer's active alert (L4.2): its
 * acknowledgement, else its latest escalation step. Undefined when neither is recorded.
 */
export function alertHandling(timer: BoardTimer | undefined): string | undefined {
  if (timer === undefined || timer.alert === 'none') return undefined;
  if (timer.acknowledgedAt !== undefined) return `Acknowledged ${clockTime(timer.acknowledgedAt)}`;
  if (timer.escalation !== undefined) {
    return `Escalated to ${timer.escalation.notify} ${clockTime(timer.escalation.at)}`;
  }
  return undefined;
}

/** Time left to the timer's breach, or how long ago it passed, or that the timer stopped. */
export function countdown(timer: BoardTimer, now: number): Countdown {
  if (timer.stoppedAt !== undefined) return { text: 'stopped', alert: timer.alert };
  const left = Date.parse(timer.breachAt) - now;
  return {
    text: left >= 0 ? `${hoursMinutes(left)} left` : `+${hoursMinutes(-left)} over`,
    alert: timer.alert,
  };
}

/** Whole years on `now`, from a FHIR date (YYYY, YYYY-MM or YYYY-MM-DD). */
export function ageInYears(birthDate: string | undefined, now: number): number | undefined {
  if (birthDate === undefined) return undefined;
  const [y, m = 1, d = 1] = birthDate.split('-').map(Number);
  if (y === undefined || Number.isNaN(y)) return undefined;
  const today = new Date(now);
  let age = today.getUTCFullYear() - y;
  if (today.getUTCMonth() + 1 < m || (today.getUTCMonth() + 1 === m && today.getUTCDate() < d)) {
    age--;
  }
  return age;
}

/** "5 min ago" style, for the latest vitals. */
export function ago(at: string | undefined, now: number): string | undefined {
  if (at === undefined) return undefined;
  const minutes = Math.max(0, Math.floor((now - Date.parse(at)) / 60_000));
  return minutes < 60 ? `${minutes} min ago` : `${hoursMinutes(minutes * 60_000)} ago`;
}

export const ZONE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  ZONES.map((z) => [z.code, z.label]),
);

export const zoneLabel = (code: string | undefined): string =>
  code === undefined || code === '' ? 'No location' : (ZONE_LABELS[code] ?? code);

export type ZoneTone = 'resus' | 'majors' | 'minors' | 'other';

const ZONE_TONES: Readonly<Record<string, ZoneTone>> = {
  RES: 'resus',
  MAJ: 'majors',
  MIN: 'minors',
};

/** The zone marker colour (Manus zone colours, without red, amber or green). */
export const zoneTone = (code: string | undefined): ZoneTone =>
  (code === undefined ? undefined : ZONE_TONES[code]) ?? 'other';

/** The ED 4-hour timer of a row, if it has started. */
export const fourHour = (row: BoardEntry): BoardTimer | undefined =>
  row.timers.find((t) => t.id === 'ed-4h');

export interface Summary {
  inDepartment: number;
  /** Recorded by the rules engine (AlertRaised), not decided here. */
  breached: number;
  warning: number;
  /** Decision to admit made, still in the ED. */
  admittedInEd: number;
  /** Labs or imaging ordered and not yet back, as the source systems sent them. */
  resultsPending: number;
  longestWait?: { ms: number; row: BoardEntry };
}

export function summarise(rows: Rows, now: number): Summary {
  const list = [...rows.values()];
  let longestWait: Summary['longestWait'];
  for (const row of list) {
    if (row.arrivedAt === undefined) continue;
    const ms = now - Date.parse(row.arrivedAt);
    if (longestWait === undefined || ms > longestWait.ms) longestWait = { ms, row };
  }
  return {
    inDepartment: list.length,
    breached: list.filter((r) => fourHour(r)?.alert === 'breach').length,
    warning: list.filter((r) => fourHour(r)?.alert === 'warning').length,
    admittedInEd: list.filter((r) => r.status === 'admitted').length,
    resultsPending: list.filter((r) => r.labs.pending + r.imaging.pending > 0).length,
    ...(longestWait !== undefined && { longestWait }),
  };
}

export type StatusFilter = 'all' | 'breach' | 'warning' | 'admitted';

export interface Filters {
  zone: string;
  status: StatusFilter;
  query: string;
}

export const NO_FILTERS: Filters = { zone: 'all', status: 'all', query: '' };

/** Rows that match the zone, status and a search on name, MRN, NHS number, bed or complaint. */
export function filterRows(rows: Rows, filters: Filters): Rows {
  const q = filters.query.trim().toLowerCase();
  return new Map(
    [...rows].filter(([, row]) => {
      if (filters.zone !== 'all' && (row.location?.room ?? '') !== filters.zone) return false;
      const alert = fourHour(row)?.alert;
      if (filters.status === 'breach' && alert !== 'breach') return false;
      if (filters.status === 'warning' && alert !== 'warning') return false;
      if (filters.status === 'admitted' && row.status !== 'admitted') return false;
      if (q === '') return true;
      const p = row.patient;
      return [p?.family, p?.given, p?.mrn, p?.nhsNumber, row.location?.bed, row.complaint?.text]
        .filter((v): v is string => v !== undefined)
        .some((v) => v.toLowerCase().includes(q));
    }),
  );
}

/** Share of the 4 hours used, for the progress bar (capped a little past the end). */
export function fourHourShare(row: BoardEntry, now: number): number | undefined {
  const timer = fourHour(row);
  if (timer === undefined) return undefined;
  const start = Date.parse(timer.startedAt);
  const end = timer.stoppedAt === undefined ? now : Date.parse(timer.stoppedAt);
  return Math.min(Math.max((end - start) / (Date.parse(timer.breachAt) - start), 0), 1.15);
}

const titleCase = (s: string) =>
  s
    .toLowerCase()
    .replace(/(^|[\s'-])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toUpperCase());

/** "Keira Underhill", or "Unknown patient". */
export function displayName(patient: BoardEntry['patient']): string {
  const name = [patient?.given, patient?.family].filter((p): p is string => p !== undefined);
  return name.length === 0 ? 'Unknown patient' : titleCase(name.join(' '));
}

/** Initials for the row avatar: given then family name, or "?". */
export function initials(patient: BoardEntry['patient']): string {
  const letters = [patient?.given, patient?.family].map((n) => n?.trim()[0] ?? '').join('');
  return letters === '' ? '?' : letters.toUpperCase();
}

/** NHS numbers are read in groups of 3, 3 and 4. */
export const nhsNumber = (value: string | undefined): string | undefined =>
  value === undefined || !/^\d{10}$/.test(value)
    ? value
    : `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`;

export const sexLetter = (gender: string | undefined): string =>
  gender === 'male' ? 'M' : gender === 'female' ? 'F' : gender === undefined ? '' : 'X';

/** Clock time (HH:MM) of an instant, in the site's time zone. */
export const clockTime = (iso: string, timeZone = 'Europe/London'): string =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone });

export function labsText(row: BoardEntry): string {
  const { pending, resulted } = row.labs;
  if (pending + resulted === 0) return 'No labs';
  return [pending > 0 && `${pending} waiting`, resulted > 0 && `${resulted} back`]
    .filter(Boolean)
    .join(' · ');
}

export function imagingText(row: BoardEntry): string {
  const { pending, reported } = row.imaging;
  if (pending + reported === 0) return 'No imaging';
  return [pending > 0 && `${pending} waiting`, reported > 0 && `${reported} reported`]
    .filter(Boolean)
    .join(' · ');
}

/** A triage category as sent, e.g. "MTS 2 · Very urgent". */
export const triageText = (t: { scale: string; category: number; label: string }): string =>
  `${t.scale} ${t.category} · ${t.label}`;
